import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import './CancelAddonModal.css'

/**
 * Modal component for confirming add-on cancellation
 * Provides a branded, user-friendly confirmation dialog
 * with clear information about what happens after cancellation
 */
const CancelAddonModal = ({
  isOpen,
  addon,
  onConfirm,
  onCancel,
  isProcessing = false,
  cancellationResult = null
}) => {
  const { t } = useTranslation('subscription')
  const [showSuccess, setShowSuccess] = useState(false)

  // Handle successful cancellation display
  if (cancellationResult && !showSuccess) {
    setShowSuccess(true)
  }

  if (!isOpen) return null

  // Translation helper for add-on details
  const getTranslatedAddonDetail = (field) => {
    if (!addon) return ''
    const key = `page.addons.items.${addon.id}.${field}`
    const translated = t(key, { defaultValue: null })
    return translated !== key ? translated : addon[field]
  }

  // Format the expiration date
  const formatExpirationDate = () => {
    if (!cancellationResult?.cancellation_effective_at) return null
    const expiresAt = new Date(cancellationResult.cancellation_effective_at * 1000)
    return expiresAt.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isProcessing) {
      onCancel()
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && !isProcessing) {
      onCancel()
    }
  }

  // Success state after cancellation
  if (showSuccess && cancellationResult) {
    const formattedDate = formatExpirationDate()

    return createPortal(
      <div
        className="modal-overlay cancel-addon-modal-overlay"
        onClick={handleOverlayClick}
        onKeyDown={handleKeyDown}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cancel-addon-success-title"
      >
        <div className="modal-content cancel-addon-modal success-state">
          <div className="modal-header">
            <h2 id="cancel-addon-success-title">
              {t('page.addons.cancelModal.successTitle', { defaultValue: 'Add-on Cancelled' })}
            </h2>
            <button
              className="close-button"
              onClick={onCancel}
              aria-label={t('page.addons.cancelModal.closeAriaLabel', { defaultValue: 'Close modal' })}
            >
              x
            </button>
          </div>

          <div className="modal-body">
            <div className="success-icon-container">
              <div className="success-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>

            <h3 className="addon-name-display">{getTranslatedAddonDetail('name')}</h3>

            <div className="success-message-box">
              <div className="calendar-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </div>
              <div className="message-content">
                <p className="primary-message">
                  {t('page.addons.cancelModal.successMessage', {
                    defaultValue: 'Your add-on has been cancelled successfully.'
                  })}
                </p>
                {formattedDate && (
                  <p className="expiration-info">
                    {t('page.addons.cancelModal.activeUntil', {
                      date: formattedDate,
                      defaultValue: `You can continue using it until ${formattedDate}.`
                    })}
                  </p>
                )}
              </div>
            </div>

            <div className="info-note">
              <span className="info-icon">i</span>
              <p>
                {t('page.addons.cancelModal.resubscribeNote', {
                  defaultValue: 'You can resubscribe to this add-on at any time from the subscription page.'
                })}
              </p>
            </div>
          </div>

          <div className="modal-footer">
            <button
              className="button button-primary"
              onClick={onCancel}
            >
              {t('page.addons.cancelModal.doneButton', { defaultValue: 'Done' })}
            </button>
          </div>
        </div>
      </div>,
      document.body
    )
  }

  // Confirmation state before cancellation
  return createPortal(
    <div
      className="modal-overlay cancel-addon-modal-overlay"
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cancel-addon-modal-title"
    >
      <div className="modal-content cancel-addon-modal">
        <div className="modal-header">
          <h2 id="cancel-addon-modal-title">
            {t('page.addons.cancelModal.title', { defaultValue: 'Cancel Add-on' })}
          </h2>
          <button
            className="close-button"
            onClick={onCancel}
            disabled={isProcessing}
            aria-label={t('page.addons.cancelModal.closeAriaLabel', { defaultValue: 'Close modal' })}
          >
            x
          </button>
        </div>

        <div className="modal-body">
          <div className="warning-icon-container">
            <div className="warning-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>

          <h3 className="addon-name-display">{getTranslatedAddonDetail('name')}</h3>

          <p className="addon-price-display">
            {t('page.addons.priceFormat', {
              price: addon?.price_monthly,
              defaultValue: `+EUR${addon?.price_monthly}/Month`
            })}
          </p>

          <p className="confirmation-message">
            {t('page.addons.cancelModal.message', {
              defaultValue: 'Are you sure you want to cancel this add-on?'
            })}
          </p>

          <div className="info-box">
            <div className="info-box-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
            </div>
            <div className="info-box-content">
              <p className="info-title">
                {t('page.addons.cancelModal.infoTitle', { defaultValue: 'What happens next?' })}
              </p>
              <ul className="info-list">
                <li>
                  {t('page.addons.cancelModal.infoPoint1', {
                    defaultValue: 'The add-on remains active until the end of your current billing period'
                  })}
                </li>
                <li>
                  {t('page.addons.cancelModal.infoPoint2', {
                    defaultValue: 'You will not be charged again for this add-on'
                  })}
                </li>
                <li>
                  {t('page.addons.cancelModal.infoPoint3', {
                    defaultValue: 'You can resubscribe at any time'
                  })}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="button button-secondary"
            onClick={onCancel}
            disabled={isProcessing}
          >
            {t('page.addons.cancelModal.keepButton', { defaultValue: 'Keep Add-on' })}
          </button>
          <button
            className="button button-danger"
            onClick={onConfirm}
            disabled={isProcessing}
          >
            {isProcessing
              ? t('page.addons.cancelModal.cancellingButton', { defaultValue: 'Cancelling...' })
              : t('page.addons.cancelModal.confirmButton', { defaultValue: 'Cancel Add-on' })
            }
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default CancelAddonModal
